import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import useSnackbar from '../../../hooks/useSnackbar';
import {
  ITechnician,
  ProfessionalSkill,
} from '../../../utils/entities/interface';
import { Close } from '@mui/icons-material';
import { PrimaryButton, SecondaryButton } from '../../atoms/Button';
import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  TechnicianPosition,
  TechnicianType,
} from '../../../utils/entities/type';
import {
  CreateTechnician,
  EditTechnician,
  GetTechnician,
  UpdateProfessionalDetails,
} from '../../../services/technician';
import { ProfessionalForm } from './prof_details';
import { BasicForm } from './basic_details';

interface TechnicianAddEditProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  technician?: ITechnician | null;
  action: 'EDBAS' | 'EDPRO' | 'ADNEW';
  technicianId?: string;
}

export interface TechnicianData {
  name: string;
  phone: string;
  email: string;
  image: string | null;
  joiningDate: Date | null;
  dob: Date | null;
  type: TechnicianType;
  secondaryContactNumber: string;
  position: TechnicianPosition;
  exp: string;
  house: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
  landmark: string;
}

export type FormChangeEvent =
  | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  | SelectChangeEvent<string>;

const defaultSkills: ProfessionalSkill[] = [
  { acType: 'Split', service: false, repair: false, install: false },
  { acType: 'Window', service: false, repair: false, install: false },
  { acType: 'Cassette', service: false, repair: false, install: false },
  { acType: 'Concealed', service: false, repair: false, install: false },
  { acType: 'VRV/VRF', service: false, repair: false, install: false },
  { acType: 'Ducted', service: false, repair: false, install: false },
  { acType: 'Tower', service: false, repair: false, install: false },
];

const validTechnicianPositions: TechnicianPosition[] = [
  'TBA',
  'HELPER',
  'TECHNICIAN',
  'SENIOR TECHNICIAN',
  'SUPERVISOR',
  'MANAGER',
];

const normalizePosition = (position?: string): TechnicianPosition => {
  if (!position) return 'TBA';
  return validTechnicianPositions.includes(position as TechnicianPosition)
    ? (position as TechnicianPosition)
    : 'TBA';
};

export const AddEditTechnicianModal = ({
  open,
  onClose,
  onSaved,
  technician,
  technicianId,
  action,
}: TechnicianAddEditProps) => {
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<number>(0);

  const steps = ['Basic Detail', 'Professional Detail'];

  const [professionalSkills, setProfessionalSkills] =
    useState<ProfessionalSkill[]>(defaultSkills);

  const [formData, setFormData] = useState<TechnicianData>({
    name: '',
    phone: '',
    email: '',
    image: null,
    joiningDate: null,
    dob: null,
    type: 'ACD',
    secondaryContactNumber: '',
    position: 'TBA',
    exp: '',
    house: '',
    street: '',
    city: '',
    state: '',
    zipcode: '',
    landmark: '',
  });

  useEffect(() => {
    if (!open) return;

    if (action === 'EDBAS') {
      setActiveStep(0);
    } else if (action === 'EDPRO') {
      setActiveStep(1);
    } else {
      setActiveStep(0);
    }

    setIsEditMode(action === 'EDBAS' || action === 'EDPRO');
  }, [action, open]);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const loadTechnicianDetails = async () => {
      try {
        const id = technicianId || technician?._id;

        let technicianDetails: any = technician || {};
        let addressDetails: any = {};

        if ((action === 'EDBAS' || action === 'EDPRO') && id) {
          const result = await GetTechnician(id);

          console.log('GET TECHNICIAN RESULT ===>', result);

          technicianDetails = result?.technician || technician || {};
          addressDetails = result?.address || {};

          console.log('ADDRESS DETAILS ===>', addressDetails);
        }

        if (!isMounted) return;

        setFormData({
          name: technicianDetails?.name || '',
          phone: technicianDetails?.phoneNumber || '',
          email: technicianDetails?.email || '',
          image: technicianDetails?.profilePhoto || null,
          joiningDate: technicianDetails?.joiningDate
            ? new Date(technicianDetails.joiningDate)
            : null,
          dob: technicianDetails?.dob ? new Date(technicianDetails.dob) : null,
          type: technicianDetails?.type || 'ACD',
          secondaryContactNumber:
            technicianDetails?.secondaryContactNumber || '',
          position: normalizePosition(technicianDetails?.position),
          exp: technicianDetails?.experience || '',

          // Address prefilled from GetTechnician API
          house:
            addressDetails?.house ||
            technicianDetails?.house ||
            technicianDetails?.address?.house ||
            '',
          street:
            addressDetails?.street ||
            technicianDetails?.street ||
            technicianDetails?.address?.street ||
            '',
          city:
            addressDetails?.city ||
            technicianDetails?.city ||
            technicianDetails?.address?.city ||
            '',
          state:
            addressDetails?.state ||
            technicianDetails?.state ||
            technicianDetails?.address?.state ||
            '',
          zipcode:
            addressDetails?.zipcode ||
            technicianDetails?.zipcode ||
            technicianDetails?.address?.zipcode ||
            '',
          landmark:
            addressDetails?.landmark ||
            technicianDetails?.landmark ||
            technicianDetails?.address?.landmark ||
            '',
        });
      } catch (error) {
        console.log('Get Technician Details Error ===>', error);

        const currentTechnician = technician as any;
        const addressData =
          currentTechnician?.address ||
          currentTechnician?.technicianAddress ||
          currentTechnician ||
          {};

        if (!isMounted) return;

        setFormData({
          name: technician?.name || '',
          phone: technician?.phoneNumber || '',
          email: technician?.email || '',
          image: technician?.profilePhoto || null,
          joiningDate: technician?.joiningDate
            ? new Date(technician.joiningDate)
            : null,
          dob: technician?.dob ? new Date(technician.dob) : null,
          type: technician?.type || 'ACD',
          secondaryContactNumber: technician?.secondaryContactNumber || '',
          position: normalizePosition(technician?.position),
          exp: technician?.experience || '',
          house: addressData?.house || '',
          street: addressData?.street || '',
          city: addressData?.city || '',
          state: addressData?.state || '',
          zipcode: addressData?.zipcode || '',
          landmark: addressData?.landmark || '',
        });
      }
    };

    loadTechnicianDetails();

    return () => {
      isMounted = false;
    };
  }, [open, technician, technicianId, action]);

  useEffect(() => {
    const technicianSkills = technician?.professionalSkills;

    if (technicianSkills && Array.isArray(technicianSkills)) {
      const normalizedSkills: ProfessionalSkill[] = [
        'Split',
        'Window',
        'Cassette',
        'Concealed',
        'VRV/VRF',
        'Ducted',
        'Tower',
      ].map((type) => {
        const skill = technicianSkills.find(
          (s: ProfessionalSkill) =>
            s.acType.toLowerCase() === type.toLowerCase(),
        );

        return {
          acType: type,
          service: skill?.service || false,
          repair: skill?.repair || false,
          install: skill?.install || false,
        };
      });

      setProfessionalSkills(normalizedSkills);
    } else {
      setProfessionalSkills(defaultSkills);
    }
  }, [technician, technicianId]);

  const handleChange = (e: FormChangeEvent) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        name === 'dob' || name === 'joiningDate'
          ? value
            ? new Date(value)
            : null
          : value,
    }));
  };

  const handleImageChange = (url: string) => {
    setFormData((prev) => ({ ...prev, image: url }));
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
  };

  const validatePincode = () => {
    if (!/^\d{6}$/.test(formData.zipcode)) {
      showSnackbar('Pincode must be 6 digits.', 'error');
      return false;
    }

    return true;
  };

  const validateBasicFields = () => {
    const requiredFields: { field: keyof TechnicianData; name: string }[] =
      isEditMode
        ? [
            { field: 'name', name: 'Technician Name' },
            { field: 'phone', name: 'Contact Number' },
            { field: 'type', name: 'Type' },
            { field: 'dob', name: 'Date of Birth' },
            { field: 'street', name: 'Street' },
            { field: 'city', name: 'City' },
            { field: 'state', name: 'State' },
            { field: 'zipcode', name: 'Pincode' },
          ]
        : [
            { field: 'name', name: 'Technician Name' },
            { field: 'phone', name: 'Contact Number' },
            { field: 'type', name: 'Type' },
            { field: 'position', name: 'Position' },
            { field: 'dob', name: 'Date of Birth' },
            { field: 'street', name: 'Street' },
            { field: 'city', name: 'City' },
            { field: 'state', name: 'State' },
            { field: 'zipcode', name: 'Pincode' },
          ];

    if (isEditMode && formData.name === 'UNSET') {
      showSnackbar('Kindly update the technician name', 'error');
      return false;
    }

    const missingField = requiredFields.find(({ field }) => {
      const value = formData[field];

      if (typeof value === 'string') {
        return value.trim() === '';
      }

      return !value;
    });

    if (missingField) {
      showSnackbar(
        `Kindly fill the required field: ${missingField.name}`,
        'error',
      );
      return false;
    }

    return validatePincode();
  };

  const getBasicPayload = () => {
    return {
      name: formData.name.trim(),
      email: formData.email.trim(),
      profilePhoto: formData.image ? String(formData.image) : '',
      joiningDate: formData.joiningDate as Date,
      type: formData.type,
      position: formData.position,
      secondaryContactNumber: formData.secondaryContactNumber.trim(),
      dob: formData.dob as Date,
      phoneNumber: formData.phone.trim(),
      experience: formData.exp.trim(),

      // Address fields
      house: formData.house.trim(),
      street: formData.street.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      zipcode: formData.zipcode.trim(),
      landmark: formData.landmark.trim(),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateBasicFields()) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const basicPayload = getBasicPayload();

      console.log('TECHNICIAN PAYLOAD ===>', basicPayload);

      const status =
        isEditMode && technician && technician._id
          ? await EditTechnician({
              technicianId: technician._id,
              ...basicPayload,
            })
          : await CreateTechnician({
              ...basicPayload,
              professionalSkills: Array.isArray(professionalSkills)
                ? professionalSkills
                : [],
            });

      if ((status === 200 && isEditMode) || (status === 201 && !isEditMode)) {
        showSnackbar(
          isEditMode
            ? 'Technician Updated successfully!'
            : 'Technician created successfully!',
          'success',
        );

        setTimeout(() => {
          onClose();
          resetForm();
          onSaved();
        }, 1500);
      } else {
        showSnackbar('Something went wrong. Please try again.', 'error');
      }
    } catch (error: any) {
      showSnackbar(
        `Error: ${error?.response?.data?.message || error.message}`,
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = () => {
    const requiredFields: { field: keyof TechnicianData; name: string }[] = [
      { field: 'name', name: 'Technician Name' },
      { field: 'phone', name: 'Contact Number' },
      { field: 'type', name: 'Type' },
      { field: 'dob', name: 'Date of Birth' },
      { field: 'street', name: 'Street' },
      { field: 'city', name: 'City' },
      { field: 'state', name: 'State' },
      { field: 'zipcode', name: 'Pincode' },
    ];

    const missingField = requiredFields.find(({ field }) => {
      const value = formData[field];

      if (typeof value === 'string') {
        return value.trim() === '';
      }

      return !value;
    });

    if (missingField) {
      setIsLoading(false);
      showSnackbar(
        `Kindly fill the required field: ${missingField.name}`,
        'error',
      );
      return;
    }

    if (!validatePincode()) {
      setIsLoading(false);
      return;
    }

    setActiveStep(1);
  };

  const handleSkillChange = (
    acType: string,
    field: keyof Omit<ProfessionalSkill, 'acType'>,
    value: boolean,
  ) => {
    setProfessionalSkills((current) =>
      current.map((skill) =>
        skill.acType === acType ? { ...skill, [field]: value } : skill,
      ),
    );
  };

  const handleProDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (!technicianId) {
      setIsLoading(false);
      showSnackbar('Invalid Technician', 'error');
      return;
    }

    if (normalizePosition(formData.position) === 'TBA') {
      setIsLoading(false);
      showSnackbar('Please select a valid position before saving.', 'error');
      return;
    }

    try {
      const status = await UpdateProfessionalDetails({
        technicianId,
        professionalSkills,
        position: normalizePosition(formData.position),
        exp: formData.exp,
      });

      if (status === 200) {
        showSnackbar('Professional details updated successfully!', 'success');

        setTimeout(() => {
          onClose();
          resetForm();
          onSaved();
        }, 1200);
      }
    } catch (error: any) {
      showSnackbar(
        `Error: ${
          error?.response?.data?.message || error.message || 'Unknown error'
        }`,
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      image: null,
      joiningDate: null,
      dob: null,
      type: 'ACD',
      secondaryContactNumber: '',
      position: 'TBA',
      exp: '',
      house: '',
      street: '',
      city: '',
      state: '',
      zipcode: '',
      landmark: '',
    });

    setProfessionalSkills(defaultSkills);
    setActiveStep(0);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        sx={{ borderRadius: 10 }}
        PaperProps={{
          sx: {
            borderRadius: 10,
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            border: '1px solid #E31E25',
            borderRadius: 10,
            overflow: 'auto',
          }}
        >
          {SnackbarComponent}

          <DialogTitle
            sx={{
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#fff',
              backgroundColor: '#181818',
              padding: '16px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 4,
                flex: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                }}
              >
                {isEditMode ? 'Edit' : 'Create'} Technician
              </Typography>

              {!isEditMode && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mr: 10,
                  }}
                >
                  {steps.map((label, index) => (
                    <Box
                      key={label}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          border: '1px solid red',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          backgroundColor:
                            activeStep === index ? 'red' : 'transparent',
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#fff',
                          fontWeight: activeStep === index ? 'bold' : 'normal',
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <IconButton onClick={onClose} sx={{ color: '#fff' }}>
              <Close />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ backgroundColor: '#181818', padding: '24px' }}>
            {activeStep === 0 && (
              <BasicForm
                formData={formData}
                onImageChange={handleImageChange}
                onRemoveImage={handleRemoveImage}
                handleChange={handleChange}
                setIsLoading={setIsLoading}
              />
            )}

            {activeStep === 1 && (
              <ProfessionalForm
                position={formData.position}
                experience={formData.exp}
                professionalSkills={professionalSkills}
                onSkillChange={handleSkillChange}
                handleChange={handleChange}
              />
            )}
          </DialogContent>

          <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
            {isEditMode && (
              <PrimaryButton
                onClick={
                  activeStep === 0 ? handleSubmit : handleProDetailsSubmit
                }
                title="Save"
                disabled={isLoading}
              />
            )}

            {!isEditMode && activeStep === 0 && (
              <PrimaryButton
                onClick={handleNextStep}
                title="Next"
                disabled={isLoading}
              />
            )}

            {!isEditMode && activeStep === 1 && (
              <SecondaryButton
                onClick={() => {
                  setActiveStep(0);
                }}
                title="Back"
                disabled={isLoading}
              />
            )}

            {!isEditMode && activeStep === 1 && (
              <PrimaryButton
                onClick={handleSubmit}
                title="Save"
                disabled={isLoading}
              />
            )}
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};
